import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Add,
  DeleteOutline,
  SearchOutlined,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Refresh,
  EditOutlined,
  AccountTree,
  ArrowBackOutlined,
  History as HistoryIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import type {
  NetworkInterceptorParameterizationModel,
  ExtractedParam,
  ParameterizedRequest,
  NetworkInterceptorCapturedRequest,
} from '@/utils/networkInterceptorParameterization';
import { analyzeNetworkInterceptorRequests } from '@/utils/networkInterceptorParameterization';
import { useAppSelector } from '@/app/hooks';
// import { agentConnectionManager } from '@/services/agentConnectionManager';
// import { saveSavedFlow } from '@/utils/savedFlowsStorage';
// import { saveRunHistory, type RunHistoryEntry } from '@/utils/runHistoryStorage';
// import { setShouldRefetchSavedFlows } from '@/features/extentionTracker/slice';
import { renderMethodOrType } from '../../utils';
import DependencyGraphTab from './DependencyGraphTab';
import TimelineTab from './TimelineTab';
// import TimelineTab from './Components/TimelineTab';
// import { toast } from 'react-toastify';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Initial model - only used when drawer first opens */
  model: NetworkInterceptorParameterizationModel | null;
  /** Initial original captured requests - only used when drawer first opens */
  originalRequests?: NetworkInterceptorCapturedRequest[];
  /** Flow ID when editing an existing saved flow */
  flowId?: string;
  /** Pre-filled flow name when editing an existing saved flow */
  flowName?: string;
  /** Pre-filled flow description when editing an existing saved flow */
  flowDescription?: string;
  /** Callback to save/update the flow (used when editing existing flow) */
  onSaveFlow?: (
    name: string,
    requests: ParameterizedRequest[],
    allExtractedParams: ExtractedParam[],
    description?: string,
    projectId?: string,
    teamId?: string,
    customVariables?: Record<string, string>
  ) => void;
  /** History entry when viewing a past run (enables history mode) */
  historyEntry?: any | null;
  /** Pre-filled custom variables when editing an existing saved flow */
  customVariables?: Record<string, string>;
  reactFlow?: any;
};

function replaceVarPlaceholderEverywhere(
  requests: ParameterizedRequest[],
  oldName: string,
  newName: string
) {
  if (!oldName || !newName || oldName === newName) return requests;
  const oldPh = `{{${oldName}}}`;
  const newPh = `{{${newName}}}`;
  return requests.map((r) => ({
    ...r,
    parameterizedUrl: r.parameterizedUrl?.split(oldPh).join(newPh),
    parameterizedBody: r.parameterizedBody?.split(oldPh).join(newPh) ?? null,
    parameterizedHeaders: (r.parameterizedHeaders || []).map((h) => ({
      ...h,
      value: h?.value?.split(oldPh).join(newPh),
    })),
    usedParams: r.usedParams.map((u) => (u.name === oldName ? { ...u, name: newName } : u)),
    extractedParams: r.extractedParams.map((e) =>
      e.name === oldName ? { ...e, name: newName } : e
    ),
  }));
}

type Cookie = {
  name: string;
  value: string;
  domain: string;
  path: string;
  expires: string;
  enabled: boolean;
};

// Component to render a single parameter with hover popup for editing
const ParameterChip = ({
  paramName,
  runVars,
  onUpdateVar,
  extractionPath,
}: {
  paramName: string;
  runVars: Record<string, string>;
  onUpdateVar: (name: string, value: string) => void;
  extractionPath: string;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [localValue, setLocalValue] = useState('');

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setLocalValue(runVars[paramName] || '');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
    onUpdateVar(paramName, newValue);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <span
        onMouseEnter={handleMouseEnter}
        style={{
          color: '#f57c00',
          backgroundColor: 'rgba(245, 124, 0, 0.12)',
          borderRadius: '3px',
          padding: '0 2px',
          cursor: 'pointer',
        }}
      >
        {`{{${paramName}}}`}
      </span>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            onMouseLeave: handleClose,
            sx: {
              p: 1.5,
              mt: 0.5,
              minWidth: 250,
              boxShadow: 3,
            },
          },
        }}
      >
        <Typography
          variant="button"
          sx={{ color: 'text.secondary', mb: 0.5, display: 'block', fontWeight: 'bold' }}
        >
          {paramName}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}
        >
          {extractionPath}
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={localValue}
          onChange={handleChange}
          placeholder="Enter value..."
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            },
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </Popover>
    </>
  );
};

// Helper function to highlight parameters (text within double curly braces) in orange
const HighlightedUrl = ({
  url,
  runVars,
  onUpdateVar,
  extractionPaths,
}: {
  url: string;
  runVars: Record<string, string>;
  onUpdateVar: (name: string, value: string) => void;
  extractionPaths: ExtractedParam[];
}) => {
  const parts = url.split(/(\{\{[^}]+\}\})/g);
  return (
    <>
      {parts.map((part, index) =>
        part.startsWith('{{') && part.endsWith('}}') ? (
          <ParameterChip
            key={index}
            paramName={part.slice(2, -2)}
            runVars={runVars}
            onUpdateVar={onUpdateVar}
            extractionPath={
              extractionPaths.find((p) => p.name === part.slice(2, -2))
                ?.extractionPath
            }
          />
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </>
  );
};

const AnalyzeAndParameterizeDrawer = ({
  open,
  onClose,
  model,
  originalRequests,
  flowId,
  flowName: initialFlowName,
  historyEntry,
  customVariables: initialCustomVariables,
  reactFlow,
}: Props) => {
  const { shouldCallAgent } = useAppSelector(
    (state) => state.appGlobal
  );
  const isElectron = Boolean(window.electron);
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ParameterizedRequest[]>([]);
  const [allExtracted, setAllExtracted] = useState<ExtractedParam[]>([]);
  const [orderedOriginalRequests, setOrderedOriginalRequests] = useState<
    NetworkInterceptorCapturedRequest[]
  >([]);
  const [hasReordered, setHasReordered] = useState(false);
  // Track if we've initialized from props - ensures we only load once per drawer open
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [runVars, setRunVars] = useState<Record<string, string>>({});
  // const [maskSecrets, setMaskSecrets] = useState(true);
  const [highlightedParam, setHighlightedParam] = useState<string | null>(null);
  const [runExecutedAt, setRunExecutedAt] = useState<string | null>(null);
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(new Set());
  const [extractedParamsExpanded, setExtractedParamsExpanded] = useState(true);
  const [runResults, setRunResults] = useState<
    Record<
      string,
      {
        ok: boolean;
        status?: number;
        statusText?: string;
        durationMs: number;
        startedAt?: string;
        finishedAt?: string;
        responsePreview?: string;
        error?: string;
      }
    >
  >({});
  const [activeTab, setActiveTab] = useState<'edit' | 'dependency' | 'timeline'>('edit');
  const [flowName, setFlowName] = useState(initialFlowName || '');
  // const [flowDescription, setFlowDescription] = useState(initialFlowDescription || '');
  const [customVariables, setCustomVariables] = useState<Record<string, string>>({});
  const [customVariablesExpanded, setCustomVariablesExpanded] = useState(true);

  // Check if we're editing an existing flow
  const isEditingExistingFlow = Boolean(flowId);

  // Check if we're viewing a historical run
  const isHistoryMode = Boolean(historyEntry);

  // Initialize from props only when drawer first opens
  // After that, the drawer maintains its own state completely (decoupled from parent)
  useEffect(() => {
    if (!open) {
      // Reset initialization flag when drawer closes so next open will re-initialize
      setIsInitialized(false);
      return;
    }

    // Only initialize once per drawer open
    if (isInitialized) return;

    const reqs = model?.requests || [];
    setRequests(reqs);
    setAllExtracted(model?.allExtractedParams || []);
    setRunVars({});
    setRunResults({});
    setCustomVariables({});
    // Expand first request by default
    setExpandedRequests(reqs.length > 0 ? new Set([reqs[0].uniqueId]) : new Set());
    setExtractedParamsExpanded(true);
    setCustomVariablesExpanded(true);
    // Track original requests order for re-analysis
    if (originalRequests) {
      // Match order to the model's requests order
      const orderedOriginals = reqs
        .map((r) => originalRequests.find((o) => o.uniqueId === r.uniqueId))
        .filter((o): o is NetworkInterceptorCapturedRequest => !!o);
      setOrderedOriginalRequests(orderedOriginals);
    }
    setHasReordered(false);

    // Initialize flow name, description, and custom variables from props (for editing existing flows)
    if (initialFlowName) {
      setFlowName(initialFlowName);
    }
    if (initialCustomVariables) {
      setCustomVariables(initialCustomVariables);
    }

    setIsInitialized(true);
  }, [
    open,
    model,
    originalRequests,
    isInitialized,
    initialFlowName,
    initialCustomVariables,
  ]);

  // Pre-populate results when viewing history
  useEffect(() => {
    if (historyEntry && open) {
      // Pre-populate run results from history
      setRunResults(historyEntry.response.results);
      setRequests(historyEntry.response.requests || []);
      setAllExtracted(historyEntry.response.allExtractedParams || []);
      setRunVars(historyEntry.response.extractedVars || {});
      setRunExecutedAt(historyEntry.response?.executedAt || null);
      // Load custom variables if they exist in history
      if ((historyEntry.response as any)?.customVariables) {
        setCustomVariables((historyEntry.response as any).customVariables);
      }
      // Expand all requests to show results
      setExpandedRequests(
        new Set(historyEntry.response?.requests?.map((r) => r.uniqueId) || [])
      );
    }
  }, [historyEntry, open]);

  // const isSensitiveHeaderKey = (key: string) => {
  // 	const k = String(key || '').toLowerCase();
  // 	return (
  // 		k.includes('token') ||
  // 		k.includes('authorization') ||
  // 		k.includes('x-api-key') ||
  // 		k.includes('apikey') ||
  // 		k.includes('api-key')
  // 	);
  // };

  const updateHeader = (requestUniqueId: string, idx: number, patch: any) => {
    setRequests((prev) =>
      prev.map((r) => {
        if (r.uniqueId !== requestUniqueId) return r;
        const headers = Array.isArray((r as any).parameterizedHeaders)
          ? (r as any).parameterizedHeaders
          : [];
        const next = headers.map((h: any, i: number) =>
          i === idx ? { ...h, ...patch } : h
        );
        return { ...r, parameterizedHeaders: next } as any;
      })
    );
  };

  const addHeaderRow = (requestUniqueId: string) => {
    setRequests((prev) =>
      prev.map((r) => {
        if (r.uniqueId !== requestUniqueId) return r;
        const headers = Array.isArray((r as any).parameterizedHeaders)
          ? (r as any).parameterizedHeaders
          : [];
        return {
          ...r,
          parameterizedHeaders: [...headers, { key: '', value: '', enabled: true }],
        } as any;
      })
    );
  };

  const removeHeaderRow = (requestUniqueId: string, idx: number) => {
    setRequests((prev) =>
      prev.map((r) => {
        if (r.uniqueId !== requestUniqueId) return r;
        const headers = Array.isArray((r as any).parameterizedHeaders)
          ? (r as any).parameterizedHeaders
          : [];
        return {
          ...r,
          parameterizedHeaders: headers.filter((_: any, i: number) => i !== idx),
        } as any;
      })
    );
  };

  // const usedParamNames = useMemo(() => {
  // 	const set = new Set<string>();
  // 	requests.forEach((r) => r.usedParams.forEach((u) => set.add(u.name)));
  // 	return Array.from(set).sort();
  // }, [requests]);

  const extractedParamNames = useMemo(() => {
    const set = new Set<string>();
    allExtracted.forEach((p) => set.add(p.name));
    return Array.from(set).sort();
  }, [allExtracted]);

  // Compute which requests extract and use the highlighted param
  const highlightInfo = useMemo(() => {
    if (!highlightedParam)
      return { extractorIds: new Set<string>(), userIds: new Set<string>() };
    const extractorIds = new Set<string>();
    const userIds = new Set<string>();
    requests.forEach((r) => {
      if (r.extractedParams.some((p) => p.name === highlightedParam)) {
        extractorIds.add(r.uniqueId);
      }
      if (r.usedParams.some((u) => u.name === highlightedParam)) {
        userIds.add(r.uniqueId);
      }
    });
    return { extractorIds, userIds };
  }, [highlightedParam, requests]);

  const toggleHighlight = (paramName: string) => {
    setHighlightedParam((prev) => (prev === paramName ? null : paramName));
  };

  const toggleRequestExpanded = (requestId: string) => {
    setExpandedRequests((prev) => {
      const next = new Set(prev);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  };

  const isRequestHighlighted = (requestId: string) => {
    if (!highlightedParam) return false;
    return highlightInfo.extractorIds.has(requestId) || highlightInfo.userIds.has(requestId);
  };

  const getRequestHighlightType = (requestId: string): 'extractor' | 'user' | null => {
    if (!highlightedParam) return null;
    if (highlightInfo.extractorIds.has(requestId)) return 'extractor';
    if (highlightInfo.userIds.has(requestId)) return 'user';
    return null;
  };

  const renameVariable = (oldName: string, newNameRaw: string) => {
    const newName = newNameRaw.trim();
    if (!newName) return;
    // update per-request references first
    setRequests((prev) => replaceVarPlaceholderEverywhere(prev, oldName, newName));
    // update global extracted param list
    setAllExtracted((prev) =>
      prev.map((p) => (p.name === oldName ? { ...p, name: newName } : p))
    );
  };

  const setExtractionPath = (paramName: string, newPath: string) => {
    setAllExtracted((prev) =>
      prev.map((p) => (p.name === paramName ? { ...p, extractionPath: newPath } : p))
    );
    setRequests((prev) =>
      prev.map((r) => ({
        ...r,
        extractedParams: r.extractedParams.map((p) =>
          p.name === paramName ? { ...p, extractionPath: newPath } : p
        ),
      }))
    );
  };

  const getExtractedParamByName = (name: string) => allExtracted.find((p) => p.name === name);

  // Compute connections between requests (for visualization)
  const parameterConnections = useMemo(() => {
    const connections: Array<{
      id: string;
      fromRequestId: string;
      toRequestId: string;
      paramName: string;
      fromIndex: number;
      toIndex: number;
    }> = [];

    requests.forEach((sourceReq, sourceIdx) => {
      // For each param extracted by this request
      sourceReq.extractedParams.forEach((extractedParam) => {
        // Find requests that use this param
        requests.forEach((targetReq, targetIdx) => {
          if (targetReq.uniqueId === sourceReq.uniqueId) return;
          const usesParam = targetReq.usedParams.some(
            (u) => u.name === extractedParam.name
          );
          if (usesParam) {
            connections.push({
              id: `${sourceReq.uniqueId}-${targetReq.uniqueId}-${extractedParam.name}`,
              fromRequestId: sourceReq.uniqueId,
              toRequestId: targetReq.uniqueId,
              paramName: extractedParam.name,
              fromIndex: sourceIdx,
              toIndex: targetIdx,
            });
          }
        });
      });
    });

    return connections;
  }, [requests]);

  const makeUniqueVarName = (base: string) => {
    const normalized = base.replace(/[^a-zA-Z0-9_]/g, '') || 'var';
    let candidate = normalized;
    let i = 2;
    const exists = (n: string) => allExtracted.some((p) => p.name === n);
    while (exists(candidate)) {
      candidate = `${normalized}${i++}`;
    }
    return candidate;
  };

  const addExtractedParamForRequest = (requestUniqueId: string) => {
    const name = makeUniqueVarName('var');
    const newParam: ExtractedParam = {
      name,
      producerRequestUniqueId: requestUniqueId,
      extractionPath: '$.id',
      enabled: true,
    };

    setAllExtracted((prev) => [...prev, newParam]);
    setRequests((prev) =>
      prev.map((r) =>
        r.uniqueId === requestUniqueId
          ? { ...r, extractedParams: [...(r.extractedParams || []), newParam] }
          : r
      )
    );
  };

  const removeExtractedParamFromRequest = (requestUniqueId: string, paramName: string) => {
    setRequests((prev) =>
      prev.map((r) =>
        r.uniqueId === requestUniqueId
          ? {
            ...r,
            extractedParams: (r.extractedParams || []).filter(
              (p) => p.name !== paramName
            ),
          }
          : r
      )
    );

    // Remove from global list ONLY if no request references it anymore as an extracted param
    setAllExtracted((prev) => {
      const stillReferenced = requests.some((r) =>
        (r.extractedParams || []).some(
          (p) => p.name === paramName && r.uniqueId !== requestUniqueId
        )
      );
      if (stillReferenced) return prev;
      return prev.filter((p) => p.name !== paramName);
    });
  };

  const moveRequest = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= requests.length) return;

    // Update requests order
    setRequests((prev) => {
      const newRequests = [...prev];
      [newRequests[index], newRequests[newIndex]] = [
        newRequests[newIndex],
        newRequests[index],
      ];
      return newRequests;
    });

    // Update original requests order for re-analysis
    setOrderedOriginalRequests((prev) => {
      if (prev.length !== requests.length) return prev;
      const newOriginals = [...prev];
      [newOriginals[index], newOriginals[newIndex]] = [
        newOriginals[newIndex],
        newOriginals[index],
      ];
      return newOriginals;
    });

    setHasReordered(true);
  };

  const handleReanalyze = () => {
    if (orderedOriginalRequests.length === 0) return;

    // Re-run analysis with the new order
    const newModel = analyzeNetworkInterceptorRequests(orderedOriginalRequests);

    // Update local state only - drawer is fully decoupled from parent after initial load
    setRequests(newModel.requests);
    setAllExtracted(newModel.allExtractedParams);
    setRunVars({});
    setRunResults({});
    setHasReordered(false);
  };

  // Custom variables management
  const addCustomVariable = () => {
    const baseName = 'customVar';
    let name = baseName;
    let counter = 1;
    while (customVariables[name] !== undefined) {
      name = `${baseName}${counter++}`;
    }
    setCustomVariables((prev) => ({ ...prev, [name]: '' }));
  };

  const updateCustomVariableName = (oldName: string, newName: string) => {
    const trimmedName = newName.trim();
    if (!trimmedName || trimmedName === oldName) return;
    if (customVariables[trimmedName] !== undefined && trimmedName !== oldName) {
      // Name already exists
      return;
    }
    setCustomVariables((prev) => {
      const updated = { ...prev };
      const value = updated[oldName];
      delete updated[oldName];
      updated[trimmedName] = value;
      return updated;
    });
  };

  const updateCustomVariableValue = (name: string, value: string) => {
    setCustomVariables((prev) => ({ ...prev, [name]: value }));
  };

  const deleteCustomVariable = (name: string) => {
    setCustomVariables((prev) => {
      const updated = { ...prev };
      delete updated[name];
      return updated;
    });
  };

  const applyVars = (template: string, vars: Record<string, string>) => {
    if (!template) return template;
    // Merge custom variables with runtime variables (runtime variables take precedence)
    const mergedVars = { ...customVariables, ...vars };
    return template.replace(/\{\{([^}]+)\}\}/g, (match, rawName) => {
      const name = String(rawName).trim();
      return mergedVars[name] !== undefined ? String(mergedVars[name]) : match;
    });
  };

  const extractFromSimpleJsonPath = (obj: any, path: string) => {
    // Supports: $.a.b[0].c OR $.a.b.0.c (best-effort). No filters, no wildcards.
    if (!path) return undefined;
    const p = path.trim();
    if (!p.startsWith('$')) return undefined;
    let cursor: any = obj;
    const normalized = p
      .replace(/^\$\./, '')
      .replace(/^\$/, '')
      .replace(/\[(\d+)\]/g, '.$1');
    const parts = normalized.split('.').filter(Boolean);
    for (const part of parts) {
      if (cursor === null || cursor === undefined) return undefined;
      // numeric index
      if (/^\d+$/.test(part)) {
        const idx = Number(part);
        cursor = Array.isArray(cursor) ? cursor[idx] : undefined;
      } else {
        cursor = cursor?.[part];
      }
    }
    return cursor;
  };

  /**
   * Get applicable cookies for a given URL from the local cookie jar
   * Matches domain, path, and checks expiry (similar to useRequestSender)
   */
  const getApplicableCookies = (url: string, cookieJar: Cookie[]): Cookie[] => {
    try {
      const urlObj = new URL(url);
      return cookieJar.filter((cookie) => {
        if (!cookie.enabled) return false;

        // Check domain match
        const cookieDomain = cookie.domain.startsWith('.')
          ? cookie.domain.slice(1)
          : cookie.domain;
        const hostMatch = urlObj.hostname.endsWith(cookieDomain);
        if (!hostMatch) return false;

        // Check path match
        const urlPath = urlObj.pathname || '/';
        const pathMatch = urlPath.startsWith(cookie.path);
        if (!pathMatch) return false;

        // Check if cookie is expired
        if (cookie.expires) {
          const expiryDate = new Date(cookie.expires);
          if (expiryDate < new Date()) return false;
        }

        return true;
      });
    } catch (e) {
      console.error('Error parsing URL for cookies:', e);
      return [];
    }
  };

  /**
   * Extract cookies from response and add/update them in the cookie jar
   * Handles both direct fetch responses and server proxy responses
   */

  const runViaServerOrAgentExecute = async (
    url: string,
    method: string,
    headers: any,
    body: any,
    useAgent: boolean = false
  ) => {
    const baseUrl = useAgent
      ? (import.meta.env.VITE_ATA_AGENT_BASE_URL as string | undefined)
      : (import.meta.env.VITE_EXPRESS_API_URL as string | undefined);

    if (!baseUrl) {
      throw new Error(
        useAgent
          ? 'Agent execute not configured (missing VITE_ATA_AGENT_BASE_URL)'
          : 'Server execute fallback not configured (missing VITE_EXPRESS_API_URL)'
      );
    }

    const payload = {
      requestData: {
        url: url,
        reqMethod: method,
        reqHeaders: Object.entries(headers || {})
          .map(([key, value]) => ({
            key,
            value,
            enabled: true,
            touched: true,
            description: '',
          }))
          .reduce((acc: any, cur: any) => {
            if (cur.key) {
              acc[cur.key] = cur.value;
            }
            return acc;
          }, {}),
        reqParams: {},
        reqBody: {
          reqType: body ? 'raw' : 'none',
          reqFormat: 'json',
          reqData: typeof body === 'string' ? body : body ? JSON.stringify(body) : '',
        },
      },
      requestObject: {
        requestData: {
          reqApiUrl: url,
          reqMethod: method,
          reqHeaders: Object.entries(headers || {})
            .map(([key, value]) => ({
              key,
              value,
              enabled: true,
              touched: true,
              description: '',
            }))
            .reduce((acc: any, cur: any) => {
              if (cur.key) {
                acc[cur.key] = cur.value;
              }
              return acc;
            }, {}),
          reqParams: {},
          reqBody: {
            reqType: body ? 'raw' : 'none',
            reqFormat: 'json',
            reqData: typeof body === 'string' ? body : body ? JSON.stringify(body) : '',
          },
        },
      },
    };

    return fetch(`${baseUrl}/request/execute`, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(payload),
    });
  };

  const handleRun = async () => {
    if (isRunning || requests.length === 0) return;
    setIsRunning(true);
    setRunResults({});
    setRunVars({});
    setRunExecutedAt(new Date().toISOString());

    const vars: Record<string, string> = {};
    const localResults: Record<string, any> = {}; // Track results locally for history save
    // Local cookie jar to maintain cookies across chained requests during this run
    let cookieJar: Cookie[] = [];

    try {
      for (const r of requests) {
        const stepStartedAtIso = new Date().toISOString();
        const method = (r.method || 'GET').toUpperCase();
        const url = applyVars(r.parameterizedUrl, vars);
        const bodyRaw = r.parameterizedBody ? applyVars(r.parameterizedBody, vars) : null;

        const headers: Record<string, string> = {};
        // If analyzer produced headers, use them (enabled only), and apply variables.
        if (
          Array.isArray((r as any).parameterizedHeaders) &&
          (r as any).parameterizedHeaders.length > 0
        ) {
          for (const h of (r as any).parameterizedHeaders) {
            const key = String(h?.key ?? '').trim();
            if (!key) continue;
            const enabled = h?.enabled !== false;
            if (!enabled) continue;
            const value = applyVars(String(h?.value ?? ''), vars);
            headers[key] = value;
          }
        }
        if (bodyRaw && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
          // Only set defaults if not already provided by captured headers
          if (!headers['Content-Type'] && !headers['content-type']) {
            headers['Content-Type'] = 'application/json; charset=utf-8';
          }
          if (!headers['Accept'] && !headers['accept']) {
            headers['Accept'] = 'application/json';
          }
        }

        // Add applicable cookies from the local cookie jar to the request
        const applicableCookies = getApplicableCookies(url, cookieJar);
        const cookieHeader = applicableCookies.map((cookie) => `${cookie.name}=${cookie.value}`).join('; ');
        if (cookieHeader) {
          // Merge with any existing Cookie header from parameterized headers
          if (headers['Cookie'] || headers['cookie']) {
            const existingCookie = headers['Cookie'] || headers['cookie'];
            headers['Cookie'] = `${existingCookie}; ${cookieHeader}`;
            delete headers['cookie']; // Normalize to 'Cookie'
          } else {
            headers['Cookie'] = cookieHeader;
          }
        }

        const startedAt = performance.now();
        let resp: Response;
        let usedServerOrAgent = false;

        // Determine if we should use direct fetch or go through agent/server
        // Logic matches useRequestSender.ts
        const isLocalhost =
          url.includes('localhost') ||
          url.includes('127.0.0.1') ||
          url.includes('0.0.0.0');

        const shouldUseDirectFetch = (isElectron || isLocalhost) && !shouldCallAgent;

        try {
          if (shouldUseDirectFetch) {
            // Direct fetch for localhost/electron when agent is not explicitly requested
            resp = await fetch(url, {
              method,
              headers,
              body:
                bodyRaw && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
                  ? bodyRaw
                  : undefined,
            });
          } else {
            // Use agent or server proxy (same logic as useRequestSender.ts)
            const useAgent =
              typeof shouldCallAgent === 'boolean'
                ? shouldCallAgent
                : false;
            usedServerOrAgent = true;
            resp = await runViaServerOrAgentExecute(
              url,
              method,
              headers,
              bodyRaw,
              useAgent
            );
          }
        } catch (e: any) {
          // Best-effort fallback through server-side execute endpoint (avoids CORS in many cases)
          const useAgent =
            typeof shouldCallAgent === 'boolean'
              ? shouldCallAgent
              : false;
          usedServerOrAgent = true;
          resp = await runViaServerOrAgentExecute(
            url,
            method,
            headers,
            bodyRaw,
            useAgent
          );
        }
        const durationMs = Math.round(performance.now() - startedAt);
        const stepFinishedAtIso = new Date().toISOString();

        // Extract cookies from response and update the local cookie jar
        // This allows subsequent requests to use cookies set by previous responses
        cookieJar = resp.headers.get('set-cookie')?.split(',').map((cookie: string) => {
          const [name, value] = cookie.split('=');
          return { name, value, domain: '', path: '', expires: '', enabled: true };
        }) || [];

        const contentType = resp.headers.get('content-type')?.toLowerCase() || '';
        let responseText = '';
        let responseJson: any = undefined;

        try {
          if (contentType.includes('application/json')) {
            responseJson = await resp.json();
            responseText = JSON.stringify(responseJson, null, 2);
          } else {
            responseText = await resp.text();
            try {
              responseJson = JSON.parse(responseText);
            } catch {
              // ignore
            }
          }
        } catch (e: any) {
          responseText = e instanceof Error ? e.message : String(e);
        }

        // Apply extractions for this step (enabled only)
        const extractedForStep = (r.extractedParams || []).filter((p) => {
          const global = getExtractedParamByName(p.name);
          return (global?.enabled ?? p.enabled) !== false;
        });

        for (const p of extractedForStep) {
          const global = getExtractedParamByName(p.name);
          const path = global?.extractionPath ?? p.extractionPath;
          const val = extractFromSimpleJsonPath(responseJson, path);
          if (val !== undefined && val !== null) {
            vars[p.name] = String(val);
          }
        }

        setRunVars({ ...vars });
        const resultEntry = {
          ok: resp.ok,
          status: resp.status,
          statusText: usedServerOrAgent
            ? `${resp.statusText || ''} (via ${typeof shouldCallAgent === 'boolean'
              ? shouldCallAgent
                ? 'agent'
                : 'server'
              : false
                ? 'agent'
                : 'server'
            })`
            : resp.statusText,
          durationMs,
          startedAt: stepStartedAtIso,
          finishedAt: stepFinishedAtIso,
          responsePreview: responseText?.slice(0, 8000) || '',
        };
        localResults[r.uniqueId] = resultEntry;
        setRunResults((prev) => ({
          ...prev,
          [r.uniqueId]: resultEntry,
        }));
      }

      // // Save run history after successful execution
      // if (flowId && flowName) {
      //   try {
      //     const totalDuration = Object.values(localResults).reduce(
      //       (sum: number, result: any) => sum + (result?.durationMs || 0),
      //       0
      //     );
      //     const allSuccessful = Object.values(localResults).every(
      //       (r: any) => r?.ok === true
      //     );

      //     await saveRunHistory({
      //       flowId,
      //       flowName: flowName || 'Unnamed Flow',
      //       totalDurationMs: totalDuration,
      //       success: allSuccessful,
      //       requestsCount: requests.length,
      //       results: localResults as any,
      //       extractedVars: vars,
      //       requests,
      //       allExtractedParams: allExtracted,
      //       projectId: activeProject?._id || '',
      //       teamId: activeTeam?._id || '',
      //       customVariables,
      //     } as any);
      //   } catch (historyError) {
      //     console.error('Failed to save run history:', historyError);
      //     // Don't show error to user - history save failure shouldn't block flow execution
      //   }
      // }
    } catch (e: any) {
      const message = e instanceof Error ? e.message : String(e);
      // Store a generic error (and keep any partial results)
      setRunResults((prev) => ({
        ...prev,
        __flow__: { ok: false, durationMs: 0, error: message },
      }));

      // Save failed run history with partial results
      // if (flowId && flowName) {
      //   try {
      //     const totalDuration = Object.values(localResults).reduce(
      //       (sum: number, result: any) => sum + (result?.durationMs || 0),
      //       0
      //     );

      //     await saveRunHistory({
      //       flowId,
      //       flowName: flowName || 'Unnamed Flow',
      //       totalDurationMs: totalDuration,
      //       success: false,
      //       requestsCount: requests.length,
      //       results: {
      //         ...localResults,
      //         __flow__: { ok: false, durationMs: 0, error: message },
      //       } as any,
      //       extractedVars: vars,
      //       requests,
      //       allExtractedParams: allExtracted,
      //       projectId: activeProject?._id || '',
      //       teamId: activeTeam?._id || '',
      //       customVariables,
      //     } as any);
      //   } catch (historyError) {
      //     console.error('Failed to save run history:', historyError);
      //   }
      // }
    } finally {
      setIsRunning(false);
    }
  };


  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'background.paper',
        }}
        height="53px"
      >
        <IconButton onClick={onClose}>
          <ArrowBackOutlined />
        </IconButton>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {isEditingExistingFlow && flowName ? flowName : 'Analyze & Parameterize'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {requests.length} request(s) · {extractedParamNames.length} extracted
            param(s) · {parameterConnections.length} connection(s)
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isHistoryMode && historyEntry && (
            <Chip
              label={`📜 Historical Run - ${new Date(
                historyEntry.response?.executedAt
              ).toLocaleString()}`}
              size="small"
              color="info"
              sx={{
                fontWeight: 600,
              }}
            />
          )}
          {highlightedParam && (
            <Chip
              label={`Tracing: {{${highlightedParam}}}`}
              size="small"
              onDelete={() => setHighlightedParam(null)}
              sx={{
                fontFamily: 'monospace',
                backgroundColor: 'warning.light',
                fontWeight: 600,
              }}
            />
          )}
          {hasReordered && orderedOriginalRequests.length > 0 && (
            <Tooltip title="Re-analyze with new order" arrow>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Refresh />}
                onClick={handleReanalyze}
                sx={{ textTransform: 'none' }}
              >
                Re-analyze
              </Button>
            </Tooltip>
          )}
          {flowId && !isHistoryMode && (
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => navigate(`/api-lab/saved-flows/${flowId}/history`)}
              sx={{ textTransform: 'none' }}
            >
              History
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleRun}
            disabled={isRunning || requests.length === 0}
          >
            {isRunning ? 'Running…' : 'RUN'}
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            minHeight: 42,
            '& .MuiTab-root': {
              minHeight: 42,
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          <Tab
            value="edit"
            label="Edit"
            icon={<EditOutlined sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
          <Tab
            value="dependency"
            label="Dependency"
            icon={<AccountTree sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
          <Tab
            value="timeline"
            label="Timeline"
            icon={<TimelineIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Content - all tabs stay mounted, hidden via display to avoid remounting */}
      <Box sx={{ display: activeTab === 'dependency' ? 'flex' : 'none', flex: 1, overflow: 'hidden' }}>
        <DependencyGraphTab
          active={activeTab === 'dependency'}
          reactFlow={reactFlow}
          requests={requests}
          allExtracted={allExtracted}
          runVars={runVars}
          setRunVars={setRunVars}
          runResults={runResults}
          isHistoryMode={isHistoryMode}
          historyEntry={historyEntry}
          runExecutedAt={runExecutedAt}
          HighlightedUrl={HighlightedUrl}
        />
      </Box>

      {/* Timeline tab placeholder */}
      <Box sx={{ display: activeTab === 'timeline' ? 'flex' : 'none', flex: 1, overflow: 'hidden' }}>
        <TimelineTab
          active={activeTab === 'timeline'}
          requests={requests}
          runResults={runResults}
          runExecutedAt={runExecutedAt}
          isHistoryMode={isHistoryMode}
        />
      </Box>

      <Box sx={{ display: activeTab === 'edit' ? 'flex' : 'none', flex: 1, overflow: 'auto', p: 2, flexDirection: 'column' }}>
        {/* Flow run results summary */}
        {runResults.__flow__?.error && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              border: '1px solid #f3c7c7',
              background: '#fff7f7',
              borderRadius: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: 'error.main', mb: 0.5 }}
            >
              Run failed
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {runResults.__flow__.error}
            </Typography>
          </Box>
        )}
        <Box sx={{ mb: 2, minHeight: '50px', height: 'auto', overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Variables (captured during this run)
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Object.entries(runVars).map(([k, v]) => {
              const isParamHighlighted = highlightedParam === k;
              return (
                <Tooltip
                  key={k}
                  title={`${v} — Click to trace data flow`}
                  arrow
                >
                  <Chip
                    label={`${k}=${v}`}
                    size="small"
                    onClick={() => toggleHighlight(k)}
                    sx={{
                      fontFamily: 'monospace',
                      cursor: 'pointer',
                      backgroundColor: isParamHighlighted
                        ? 'warning.main'
                        : undefined,
                      color: isParamHighlighted ? '#fff' : undefined,
                      border: isParamHighlighted
                        ? '2px solid'
                        : '1px solid transparent',
                      borderColor: isParamHighlighted
                        ? 'warning.dark'
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: isParamHighlighted
                          ? 'warning.dark'
                          : 'action.hover',
                      },
                      transition: 'all 0.15s ease-in-out',
                    }}
                  />
                </Tooltip>
              );
            })}
            {Object.entries(runVars).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No variables were captured during this run.
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Custom Variables Section */}
        <Box
          sx={{
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            onClick={() => setCustomVariablesExpanded(!customVariablesExpanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              cursor: 'pointer',
              backgroundColor: 'action.hover',
              '&:hover': { backgroundColor: 'action.selected' },
              transition: 'background-color 0.15s',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Custom Variables ({Object.keys(customVariables).length})
            </Typography>
            {customVariablesExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>
          {/* Content */}
          <Collapse in={customVariablesExpanded}>
            <Box sx={{ p: 1.5, pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Define custom variables with static values to use across all requests. Use{' '}
                <code style={{ backgroundColor: 'rgba(245, 124, 0, 0.12)', padding: '2px 4px', borderRadius: '3px' }}>
                  {'{{variableName}}'}
                </code>{' '}
                in your requests.
              </Typography>
              {Object.keys(customVariables).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  No custom variables defined yet.
                </Typography>
              ) : (
                <Stack spacing={1} sx={{ mb: 1.5 }}>
                  {Object.entries(customVariables).map(([name, value], index) => (
                    <Box
                      key={'customVariable' + index + 'global'}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 40px',
                        gap: 1,
                        alignItems: 'center',
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: 'background.default',
                      }}
                    >
                      <TextField
                        size="small"
                        label="Variable Name"
                        value={name}
                        onChange={(e) =>
                          updateCustomVariableName(name, e.target.value)
                        }
                        InputProps={{
                          style: { fontFamily: 'monospace' },
                        }}
                      />
                      <TextField
                        size="small"
                        label="Value"
                        value={value}
                        onChange={(e) =>
                          updateCustomVariableValue(name, e.target.value)
                        }
                        placeholder="Enter value..."
                        InputProps={{
                          style: { fontFamily: 'monospace' },
                        }}
                      />
                      <Tooltip title="Delete variable" arrow>
                        <IconButton
                          size="small"
                          onClick={() => deleteCustomVariable(name)}
                          color="error"
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Stack>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={addCustomVariable}
                sx={{ textTransform: 'none' }}
              >
                Add Custom Variable
              </Button>
            </Box>
          </Collapse>
        </Box>

        {/* Global extracted params editor */}
        <Box
          sx={{
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            onClick={() => setExtractedParamsExpanded(!extractedParamsExpanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              cursor: 'pointer',
              backgroundColor: 'action.hover',
              '&:hover': { backgroundColor: 'action.selected' },
              transition: 'background-color 0.15s',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Extracted params ({allExtracted.length})
            </Typography>
            {extractedParamsExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>
          {/* Content */}
          <Collapse in={extractedParamsExpanded}>
            <Box sx={{ p: 1.5, pt: 1 }}>
              {allExtracted.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No parameters were auto-detected. You can still manually
                  edit each request below.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {allExtracted.map((p, index) => {
                    const isParamHighlighted = highlightedParam === p.name;
                    const usageCount = requests.filter((r) =>
                      r.usedParams.some((u) => u.name === p.name)
                    ).length;
                    return (
                      <Box
                        key={'param' + index + 'global'}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '40px 200px 1fr 80px',
                          gap: 1,
                          alignItems: 'center',
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: isParamHighlighted
                            ? 'rgba(255, 152, 0, 0.08)'
                            : 'transparent',
                          border: isParamHighlighted
                            ? '1px solid'
                            : '1px solid transparent',
                          borderColor: isParamHighlighted
                            ? 'warning.main'
                            : 'transparent',
                          transition: 'all 0.15s ease-in-out',
                        }}
                      >
                        <Tooltip
                          title="Click to trace data flow across requests"
                          arrow
                        >
                          <IconButton
                            onClick={() => toggleHighlight(p.name)}
                          >
                            <SearchOutlined sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>
                        <TextField
                          size="small"
                          label="Variable name"
                          value={p.name}
                          onChange={(e) =>
                            renameVariable(p.name, e.target.value)
                          }
                        />
                        <TextField
                          size="small"
                          label="Extraction path (guess)"
                          value={p.extractionPath}
                          onChange={(e) =>
                            setExtractionPath(
                              p.name,
                              e.target.value
                            )
                          }
                          placeholder="e.g. $.id"
                          fullWidth
                        />
                        <Tooltip
                          title={`Used in ${usageCount} request(s)`}
                          arrow
                        >
                          <Chip
                            label={`${usageCount} use${usageCount !== 1 ? 's' : ''
                              }`}
                            size="small"
                            color={
                              usageCount > 0 ? 'info' : 'default'
                            }
                            variant="outlined"
                            sx={{ fontFamily: 'monospace' }}
                          />
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Per-request editor */}
        <Stack spacing={2}>
          {requests.map((r) => {
            const highlightType = getRequestHighlightType(r.uniqueId);
            const isHighlighted = isRequestHighlighted(r.uniqueId);
            const isExpanded = expandedRequests.has(r.uniqueId);
            return (
              <Box
                key={r.uniqueId}
                sx={{
                  border: '1px solid',
                  borderColor: isHighlighted
                    ? highlightType === 'extractor'
                      ? 'success.main'
                      : 'info.main'
                    : 'divider',
                  borderRadius: 1,
                  backgroundColor: isHighlighted
                    ? highlightType === 'extractor'
                      ? 'rgba(46, 125, 50, 0.04)'
                      : 'rgba(2, 136, 209, 0.04)'
                    : 'transparent',
                  transition: 'all 0.2s ease-in-out',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: 1,
                }}
              >
                {isHighlighted && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 12,
                      fontSize: '10px',
                      fontWeight: 700,
                      backgroundColor:
                        highlightType === 'extractor'
                          ? '#2e7d32'
                          : '#0288d1',
                      color: '#fff',
                      px: 1,
                      py: 0.25,
                      borderRadius: '0 0 4px 4px',
                      zIndex: 1,
                    }}
                  >
                    {highlightType === 'extractor' ? 'SOURCE' : 'USES'}
                  </Box>
                )}

                <Box
                  onClick={() => toggleRequestExpanded(r.uniqueId)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.selected' },
                    transition: 'background-color 0.15s',
                    backgroundColor: 'action.hover',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flex: 1,
                      overflow: 'hidden',
                    }}
                  >
                    {renderMethodOrType(r.method, true)}
                    {runResults[r.uniqueId] && (
                      <Chip
                        size="small"
                        color={
                          runResults[r.uniqueId].ok
                            ? 'success'
                            : 'error'
                        }
                        label={
                          runResults[r.uniqueId].status
                            ? `${runResults[r.uniqueId].status} · ${runResults[r.uniqueId]
                              .durationMs
                            }ms`
                            : runResults[r.uniqueId].ok
                              ? 'OK'
                              : 'Error'
                        }
                      />
                    )}
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        fontFamily: 'monospace',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        fontWeight: 'bold',
                      }}
                    >
                      <HighlightedUrl
                        url={r.parameterizedUrl}
                        runVars={runVars}
                        onUpdateVar={(name, value) => {
                          setRunVars((prev) => ({
                            ...prev,
                            [name]: value,
                          }));
                        }}
                        extractionPaths={allExtracted}
                      />
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Tooltip title="Move up" arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={
                            requests.findIndex(
                              (req) => req.uniqueId === r.uniqueId
                            ) === 0
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            const index = requests.findIndex(
                              (req) => req.uniqueId === r.uniqueId
                            );
                            moveRequest(index, 'up');
                          }}
                          sx={{
                            '&:disabled': {
                              opacity: 0.3,
                            },
                          }}
                        >
                          <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down" arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={
                            requests.findIndex(
                              (req) => req.uniqueId === r.uniqueId
                            ) ===
                            requests.length - 1
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            const index = requests.findIndex(
                              (req) => req.uniqueId === r.uniqueId
                            );
                            moveRequest(index, 'down');
                          }}
                          sx={{
                            '&:disabled': {
                              opacity: 0.3,
                            },
                          }}
                        >
                          <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                </Box>

                {/* Content */}
                <Collapse in={isExpanded}>
                  <Box
                    sx={{
                      p: 2,
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'flex-start',
                        }}
                      >
                        <FormControl
                          size="small"
                          sx={{ minWidth: 110 }}
                        >
                          <Select
                            value={r.method || 'GET'}
                            onChange={(e) => {
                              const method = e.target.value;
                              setRequests((prev) =>
                                prev.map((x) =>
                                  x.uniqueId === r.uniqueId
                                    ? { ...x, method }
                                    : x
                                )
                              );
                            }}
                            sx={{
                              fontFamily: 'monospace',
                              fontWeight: 600,
                              '& .MuiSelect-select': {
                                py: 1,
                              },
                            }}
                          >
                            {[
                              'GET',
                              'POST',
                              'PUT',
                              'PATCH',
                              'DELETE',
                              'OPTIONS',
                              'HEAD',
                            ].map((m) => (
                              <MenuItem
                                key={m}
                                value={m}
                                sx={{ fontFamily: 'monospace' }}
                              >
                                {m}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Parameterized URL"
                          value={r.parameterizedUrl}
                          onChange={(e) => {
                            const v = e.target.value;
                            setRequests((prev) =>
                              prev.map((x) =>
                                x.uniqueId === r.uniqueId
                                  ? {
                                    ...x,
                                    parameterizedUrl: v,
                                  }
                                  : x
                              )
                            );
                          }}
                          fullWidth
                          size="small"
                          InputProps={{
                            style: { fontFamily: 'monospace' },
                          }}
                        />
                      </Box>

                      {/* <TextField
										label="Body (parameterized)"
										value={r.parameterizedBody ?? ''}
										onChange={(e) => {
											const v = e.target.value;
											setRequests((prev) =>
												prev.map((x) =>
													x.uniqueId === r.uniqueId
														? { ...x, parameterizedBody: v || null }
														: x
												)
											);
										}}
										fullWidth
										size="small"
										minRows={3}
										multiline
										InputProps={{
											style: { fontFamily: 'monospace' },
										}}
									/> */}

                      {r.method !== 'GET' && r.method !== 'DELETE' ? (
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1,
                          }}
                        >
                          <textarea
                            value={r.parameterizedBody ?? ''}
                            onChange={(e) => {
                              setRequests((prev) =>
                                prev.map((x) =>
                                  x.uniqueId === r.uniqueId
                                    ? { ...x, parameterizedBody: e.target.value || null }
                                    : x
                                )
                              );
                            }}
                            spellCheck={false}
                            style={{
                              width: '100%',
                              height: '200px',
                              fontFamily: 'monospace',
                              fontSize: '13px',
                              lineHeight: '1.5',
                              padding: '8px',
                              border: 'none',
                              outline: 'none',
                              resize: 'vertical',
                              backgroundColor: 'transparent',
                              color: 'inherit',
                              whiteSpace: 'pre',
                              overflowWrap: 'normal',
                              overflowX: 'auto',
                            }}
                          />
                        </Box>
                      ) : null}

                      {/* Headers */}
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600 }}
                            >
                              Headers
                            </Typography>
                            <Chip
                              size="small"
                              label={`${(r as any).parameterizedHeaders
                                ?.length || 0
                                }`}
                              sx={{ fontFamily: 'monospace' }}
                            />
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => addHeaderRow(r.uniqueId)}
                            sx={{ textTransform: 'none' }}
                          >
                            Add header
                          </Button>
                        </Box>

                        {!(r as any).parameterizedHeaders ||
                          (r as any).parameterizedHeaders.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            No headers captured for this request.
                          </Typography>
                        ) : (
                          <Stack spacing={2}>
                            {(r as any).parameterizedHeaders.map(
                              (h: any, headerIdx: number) => {
                                const key = String(
                                  h?.key ?? ''
                                );
                                const value = String(
                                  h?.value ?? ''
                                );
                                // const enabled = h?.enabled !== false;
                                return (
                                  <Box
                                    key={`${key}-${headerIdx}`}
                                    sx={{
                                      gap: 1,
                                      alignItems:
                                        'center',
                                      display: 'flex',
                                    }}
                                  >
                                    <TextField
                                      size="small"
                                      fullWidth
                                      label="Key"
                                      value={key}
                                      onChange={(e) =>
                                        updateHeader(
                                          r.uniqueId,
                                          headerIdx,
                                          {
                                            key: e
                                              .target
                                              .value,
                                          }
                                        )
                                      }
                                      InputProps={{
                                        style: {
                                          fontFamily:
                                            'monospace',
                                        },
                                      }}
                                    />
                                    <TextField
                                      size="small"
                                      label="Value"
                                      value={value}
                                      fullWidth
                                      onChange={(e) =>
                                        updateHeader(
                                          r.uniqueId,
                                          headerIdx,
                                          {
                                            value: e
                                              .target
                                              .value,
                                          }
                                        )
                                      }
                                      InputProps={{
                                        style: {
                                          fontFamily:
                                            'monospace',
                                        },
                                      }}
                                    />
                                    <Tooltip
                                      title="Remove header"
                                      arrow
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          removeHeaderRow(
                                            r.uniqueId,
                                            headerIdx
                                          )
                                        }
                                      >
                                        <DeleteOutline fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                );
                              }
                            )}
                          </Stack>
                        )}
                      </Box>

                      {runResults[r.uniqueId] && (
                        <Box
                          sx={{
                            p: 1.5,
                          }}
                        >
                          <Typography
                            // variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 0.5 }}
                          >
                            Result
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {runResults[r.uniqueId].status ?? '—'}{' '}
                            {runResults[r.uniqueId].statusText
                              ? `(${runResults[r.uniqueId]
                                .statusText
                              })`
                              : ''}{' '}
                            · {runResults[r.uniqueId].durationMs}ms
                          </Typography>
                          {runResults[r.uniqueId].error ? (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'error.main',
                                fontFamily: 'monospace',
                              }}
                            >
                              {runResults[r.uniqueId].error}
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                maxHeight: 260,
                                overflow: 'auto',
                                p: 1,
                                borderRadius: 1,
                                border: '1px solid #eaeaea',
                                background: '#fff',
                              }}
                            >
                              <pre
                                style={{
                                  margin: 0,
                                  fontFamily: 'monospace',
                                  fontSize: '13px',
                                  lineHeight: '1.5',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  maxHeight: '240px',
                                  overflow: 'auto',
                                }}
                              >
                                {(() => {
                                  try {
                                    return JSON.stringify(JSON.parse(runResults[r.uniqueId].responsePreview ?? ''), null, 2);
                                  } catch {
                                    return runResults[r.uniqueId].responsePreview ?? '';
                                  }
                                })()}
                              </pre>
                            </Box>
                          )}
                        </Box>
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Box sx={{ minWidth: 320, flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Used params ({r.usedParams.length})
                          </Typography>
                          {r.usedParams.length === 0 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              No variables used in this request.
                            </Typography>
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {r.usedParams.map((u) => {
                                const isParamHighlighted =
                                  highlightedParam === u.name;
                                return (
                                  <Tooltip
                                    key={u.name}
                                    title={`Used in: ${u.locations.join(
                                      ', '
                                    )}. Click to trace data flow.`}
                                    arrow
                                  >
                                    <Chip
                                      label={`{{${u.name}}}`}
                                      size="small"
                                      onClick={() =>
                                        toggleHighlight(
                                          u.name
                                        )
                                      }
                                      sx={{
                                        fontFamily:
                                          'monospace',
                                        cursor: 'pointer',
                                        backgroundColor:
                                          isParamHighlighted
                                            ? 'info.main'
                                            : undefined,
                                        color: isParamHighlighted
                                          ? '#fff'
                                          : undefined,
                                        border: isParamHighlighted
                                          ? '2px solid'
                                          : '1px solid transparent',
                                        borderColor:
                                          isParamHighlighted
                                            ? 'info.dark'
                                            : 'transparent',
                                        '&:hover': {
                                          backgroundColor:
                                            isParamHighlighted
                                              ? 'info.dark'
                                              : 'action.hover',
                                        },
                                        transition:
                                          'all 0.15s ease-in-out',
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            </Stack>
                          )}
                        </Box>

                        <Box sx={{ minWidth: 320, flex: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600 }}
                            >
                              Extracted params (
                              {r.extractedParams.length})
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Add />}
                              onClick={() =>
                                addExtractedParamForRequest(
                                  r.uniqueId
                                )
                              }
                            >
                              Add
                            </Button>
                          </Box>
                          {r.extractedParams.length === 0 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              No extracted params from this step.
                            </Typography>
                          ) : (
                            <Stack spacing={1}>
                              {r.extractedParams.map((p, idx) => {
                                const isParamHighlighted =
                                  highlightedParam === p.name;
                                return (
                                  <Box
                                    key={
                                      'key' +
                                      idx +
                                      r.uniqueId
                                    }
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns:
                                        '36px 1fr 1fr 36px',
                                      gap: 1,
                                      alignItems:
                                        'center',
                                      p: 1,
                                      borderRadius: 1,
                                      backgroundColor:
                                        isParamHighlighted
                                          ? 'rgba(46, 125, 50, 0.08)'
                                          : 'transparent',
                                      border: isParamHighlighted
                                        ? '1px solid'
                                        : '1px solid transparent',
                                      borderColor:
                                        isParamHighlighted
                                          ? 'success.main'
                                          : 'transparent',
                                      transition:
                                        'all 0.15s ease-in-out',
                                    }}
                                  >
                                    <Tooltip
                                      title="Click to trace data flow"
                                      arrow
                                    >
                                      {/* <Chip
																				label="SOURCE"
																				size="small"
																				onClick={() =>
																					toggleHighlight(
																						p.name
																					)
																				}
																				sx={{
																					cursor: 'pointer',
																					backgroundColor:
																						isParamHighlighted
																							? 'success.main'
																							: 'action.selected',
																					color: isParamHighlighted
																						? '#fff'
																						: undefined,
																					'&:hover': {
																						backgroundColor:
																							isParamHighlighted
																								? 'success.dark'
																								: 'action.hover',
																					},
																				}}
																			/> */}
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          toggleHighlight(
                                            p.name
                                          )
                                        }
                                        sx={{
                                          backgroundColor:
                                            isParamHighlighted
                                              ? 'success.main'
                                              : 'action.selected',
                                          color: isParamHighlighted
                                            ? '#fff'
                                            : undefined,
                                          '&:hover': {
                                            backgroundColor:
                                              isParamHighlighted
                                                ? 'success.dark'
                                                : 'action.hover',
                                          },
                                        }}
                                      >
                                        <SearchOutlined fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <TextField
                                      size="small"
                                      label="Variable name"
                                      value={p.name}
                                      onChange={(e) =>
                                        renameVariable(
                                          p.name,
                                          e.target
                                            .value
                                        )
                                      }
                                    />
                                    <TextField
                                      size="small"
                                      label="Extraction path"
                                      value={
                                        p.extractionPath
                                      }
                                      onChange={(e) =>
                                        setExtractionPath(
                                          p.name,
                                          e.target
                                            .value
                                        )
                                      }
                                    />
                                    <Tooltip
                                      title="Remove extraction from this step"
                                      arrow
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          removeExtractedParamFromRequest(
                                            r.uniqueId,
                                            p.name
                                          )
                                        }
                                      >
                                        <DeleteOutline fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                );
                              })}
                            </Stack>
                          )}
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>

  );
};

export default AnalyzeAndParameterizeDrawer;
